<template>
  <component
    :is="tag"
    v-bind="$attrs"
    v-on="$listeners"
    :target="target"
    :href="href"
    :class="[
      'v-button',
      {
        'v-button--inverted': inverted,
        'v-button--secondary': secondary,
        'v-button--small': small,
        'v-button--large': large,
        'v-button--loading': loading,
        'v-button--link': link,
        'v-button--link-icon': (link && icon) || linkIcon
      }
    ]"
    @click="goBack && onGoBack($event)"
  >
    <span v-if="loading" class="v-button__spinner">
      <span></span>
      <span></span>
      <span></span>
      <span></span>
    </span>
    <span class="v-button__content">
      <Icon v-if="linkIcon" link />
      <Icon v-if="icon" :icon="icon" />
      <slot>&nbsp;</slot>
      <Icon v-if="isExternal" external />
      <Icon v-if="isLocked" locked />
    </span>
  </component>
</template>

<script>
import Icon from "./icon";

export default {
  components: {
    Icon
  },
  props: {
    tag: {
      type: String,
      default: "a"
    },
    target: {
      type: String,
      default: null
    },
    href: {
      type: String,
      default: null
    },
    inverted: {
      type: Boolean,
      default: false
    },
    secondary: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    large: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    link: {
      type: Boolean,
      default: false
    },
    linkIcon: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    goBack: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onGoBack(event) {
      event.preventDefault();
      if (window.history.length > 2) {
        window.history.back();
      } else if (this.href) {
        window.location.href = this.href;
      }
    }
  },
  computed: {
    isLocked() {
      return !!this.href && /[?&]sc_locked/i.test(this.href);
    },
    isExternal() {
      return !!this.target && this.target === "_blank";
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.v-button {
  position: relative;
  display: inline-flex;
  border-radius: 1000px;
  cursor: pointer;
  font-weight: bold;
  background-color: theme-color("primary");
  color: #fff !important;
  padding: $spacer * 0.6875 $spacer * 1.5;
  transition: background-color 200ms ease-in-out;
  background: theme-color("primary");
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  user-select: none;
  outline: 0;
  border: 0;
  font-family: "Lota Grotesque";
  font-size: 1rem;
  line-height: 1.5rem;
  text-decoration: none;
  justify-content: center;

  &__content {
    display: flex;
    align-items: center;

    svg {
      flex-shrink: 0;
      margin-left: $spacer * 0.5;
    }
  }

  &--loading {
    pointer-events: none;
    .v-button__content {
      opacity: 0;
    }
  }

  &:hover {
    background-color: theme-color("primary");
    filter: brightness(0.95);
  }

  &:active {
    background-color: theme-color("primary");
    filter: brightness(0.8);
  }

  &--inverted {
    background-color: #fff;
    color: theme-color("primary") !important;

    &:hover {
      background-color: #fff;
      filter: brightness(0.98);
    }

    &:active {
      background-color: #fff;
      filter: brightness(0.9);
    }
  }

  &--link,
  &--link-icon {
    background: transparent;
    color: theme-color("primary") !important;
    box-shadow: none;
    padding: $spacer * 0.5 $spacer * 1;

    .v-button--secondary & {
      color: theme-color("secondary") !important;
    }

    &:hover,
    &:active {
      background: transparent;
    }
  }

  &--link-icon {
    display: inline-flex;
    justify-content: flex-start;
    padding: $spacer * 0.5 0;

    & .v-button__content {
      svg:first-child {
        margin-left: 0;
        margin-right: $spacer * 0.5;
      }
    }
  }

  &--small {
    font-size: 0.875rem;
    padding: $spacer * 0.375 $spacer;

    svg {
      transform: scale(0.8);
    }
  }

  &--large {
    font-size: 1.125rem;
    padding: $spacer * 0.875 $spacer * 2;
  }

  &--secondary {
    background-color: theme-color("secondary");
    color: #fff !important;

    &:hover {
      background-color: theme-color("secondary");
      filter: brightness(0.95);
    }

    &:active {
      background-color: theme-color("secondary");
      filter: brightness(0.8);
    }

    &.v-button--inverted {
      background-color: #fff;
      color: theme-color("secondary") !important;

      &:hover {
        background-color: #fff;
        filter: brightness(0.98);
      }

      &:active {
        background-color: #fff;
        filter: brightness(0.9);
      }
    }

    &.v-button--link,
    &.v-button--link-icon {
      background: transparent;
      color: theme-color("secondary") !important;
      box-shadow: none;
      padding: $spacer * 0.5 $spacer * 1;

      &:hover,
      &:active {
        background: transparent;
      }
    }
    &.v-button--link-icon {
      padding: $spacer * 0.5 0;
    }
  }

  &:hover:not(.v-button--link) {
    text-decoration: none !important;
  }

  &[disabled] {
    background-color: theme-color("disabled");
    color: theme-color("white") !important;
    box-shadow: none;
    cursor: not-allowed;
  }

  &__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    width: 20px;
    height: 20px;

    span {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 20px;
      height: 20px;
      border: 3px solid #fff;
      border-radius: 50%;
      animation: spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: #fff transparent transparent transparent;

      &:nth-child(1) {
        animation-delay: -0.45s;
      }

      &:nth-child(2) {
        animation-delay: -0.3s;
      }

      &:nth-child(3) {
        animation-delay: -0.15s;
      }
    }
  }

  @media print {
    // &:not(.v-button--link) {
    //   display: none;
    // }

    &.v-button--link {
      flex-wrap: wrap;

      &[href]:after {
        content: " (" attr(href) ") ";
        width: 100%;
        margin-top: -2px;
        margin-left: 32px;
        font-size: 14px;
        font-weight: normal;
        font-style: italic;
        color: theme-color("dark") !important;
      }
    }
  }
}
</style>
